import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import { useNotify } from '@/components/shared/useNotify'
import {
  VENDOR_KINDS,
  type VendorKind,
  useCreateVendor,
} from '@/hooks/useAdminVendors'

/**
 * Phase 2 vendor unification — full-page New Vendor form.
 *
 * Plan: 2026-05-04-vendor-unification-phase2.md Task 22.3.
 * - Auto-slug runs on every keystroke until the user manually edits the slug,
 *   then auto-derivation pauses for the rest of the session.
 * - On 201 → navigate to `/admin/vendors` (the list page); the new row is
 *   visible because cache invalidation runs in the hook.
 */

function deriveSlug(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120)
}

export function AdminVendorNewPage() {
  const navigate = useNavigate()
  const create = useCreateVendor()
  const { notify } = useNotify()

  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)
  const [vendorKind, setVendorKind] = useState<VendorKind>('security_product')
  const [vendorTags, setVendorTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState('')
  const [aliases, setAliases] = useState<string[]>([])
  const [aliasInput, setAliasInput] = useState('')
  const [countryIso, setCountryIso] = useState('')
  const [description, setDescription] = useState('')
  const [homepageUrl, setHomepageUrl] = useState('')
  const [notes, setNotes] = useState('')

  function handleNameChange(value: string) {
    setName(value)
    if (!slugTouched) {
      setSlug(deriveSlug(value))
    }
  }

  function handleSlugChange(value: string) {
    setSlug(value)
    setSlugTouched(true)
  }

  function addTag() {
    const trimmed = tagInput.trim()
    if (!trimmed || vendorTags.includes(trimmed)) {
      setTagInput('')
      return
    }
    setVendorTags((prev) => [...prev, trimmed])
    setTagInput('')
  }

  function removeTag(tag: string) {
    setVendorTags((prev) => prev.filter((t) => t !== tag))
  }

  function addAlias() {
    const trimmed = aliasInput.trim()
    if (!trimmed || aliases.includes(trimmed)) {
      setAliasInput('')
      return
    }
    setAliases((prev) => [...prev, trimmed])
    setAliasInput('')
  }

  function removeAlias(alias: string) {
    setAliases((prev) => prev.filter((a) => a !== alias))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim() || !slug.trim()) return
    try {
      await create.mutateAsync({
        name: name.trim(),
        slug: slug.trim(),
        vendorKind,
        vendorTags: vendorTags.length > 0 ? vendorTags : undefined,
        countryIso: countryIso.trim() || undefined,
        description: description.trim() || undefined,
        homepageUrl: homepageUrl.trim() || undefined,
        notes: notes.trim() || undefined,
        aliases: aliases.length > 0 ? aliases : undefined,
      })
      notify({ title: `${name} created`, variant: 'success' })
      navigate('/admin/vendors')
    } catch (err) {
      notify({
        title: 'Failed to create vendor',
        description: (err as Error).message,
        variant: 'error',
      })
    }
  }

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto w-full space-y-4">
      <div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/admin/vendors">
            <ArrowLeft className="h-3.5 w-3.5 mr-1" />
            Back to vendors
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>New Vendor</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="new-vendor-name">Name *</Label>
                <Input
                  id="new-vendor-name"
                  required
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-vendor-slug">Slug *</Label>
                <Input
                  id="new-vendor-slug"
                  required
                  value={slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  data-testid="vendor-new-slug-input"
                />
                {!slugTouched && (
                  <p className="text-xs text-muted-foreground">
                    Auto-derived from name. Edit to override.
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="new-vendor-kind">Kind *</Label>
                <Select
                  value={vendorKind}
                  onValueChange={(v) => setVendorKind(v as VendorKind)}
                >
                  <SelectTrigger id="new-vendor-kind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VENDOR_KINDS.map((k) => (
                      <SelectItem key={k} value={k}>
                        {k}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-vendor-country">Country (ISO α2)</Label>
                <Input
                  id="new-vendor-country"
                  placeholder="us"
                  maxLength={2}
                  value={countryIso}
                  onChange={(e) => setCountryIso(e.target.value.toLowerCase())}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-1.5 min-h-[2rem]">
                {vendorTags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="gap-1 pr-1">
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="rounded-sm hover:bg-muted"
                      aria-label={`Remove tag ${tag}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add tag…"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addTag()
                    }
                  }}
                />
                <Button type="button" variant="outline" size="sm" onClick={addTag}>
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Initial aliases (optional)</Label>
              <div className="flex flex-wrap gap-1.5 min-h-[2rem]">
                {aliases.map((alias) => (
                  <Badge key={alias} variant="secondary" className="gap-1 pr-1">
                    {alias}
                    <button
                      type="button"
                      onClick={() => removeAlias(alias)}
                      className="rounded-sm hover:bg-muted"
                      aria-label={`Remove alias ${alias}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add alias…"
                  value={aliasInput}
                  onChange={(e) => setAliasInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addAlias()
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addAlias}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="new-vendor-description">Description</Label>
              <Textarea
                id="new-vendor-description"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="new-vendor-homepage">Homepage URL</Label>
              <Input
                id="new-vendor-homepage"
                type="url"
                placeholder="https://vendor.example"
                value={homepageUrl}
                onChange={(e) => setHomepageUrl(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="new-vendor-notes">Notes</Label>
              <Textarea
                id="new-vendor-notes"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="outline" asChild>
                <Link to="/admin/vendors">Cancel</Link>
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Create Vendor'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
